import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useT } from "../../lib/i18n";
import { useSeo } from "../../lib/seo";
import { ExpressForm } from "../../components/form/ExpressForm";
import { Reviews } from "../../components/site/Reviews";
import { GoogleBadge } from "../../components/site/GoogleBadge";
import { LANDING_PRODUCTS, LANDING_CITIES, landingFor } from "../../content/landing";
import { ZONES } from "../../content/zones";
import { NotFound } from "./Pages";

export default function Landing() {
  const { slug } = useParams();
  const { t } = useT();
  const l = landingFor(slug);
  const title = l ? `${l.p.label} à ${l.z.name}` : "";
  useSeo({
    title: l ? `${title} — broderie & impression | Atelier KOUOH` : "Page introuvable", path: `/personnalisation/${slug}`,
    description: l ? `${l.p.label} à ${l.z.name} : ${l.p.plural} brodés ou imprimés à votre logo par l'atelier KOUOH (Nice). Dès 10 pièces, devis gratuit sous 24 h.` : "",
    jsonLd: l ? { "@context": "https://schema.org", "@graph": [
      { "@type": "Service", name: title, provider: { "@type": "LocalBusiness", name: "Atelier KOUOH", address: { "@type": "PostalAddress", addressLocality: "Nice", addressCountry: "FR" } }, areaServed: { "@type": "City", name: l.z.name }, serviceType: "Personnalisation textile" },
      { "@type": "FAQPage", mainEntity: l.p.faq.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
    ] } : null,
    image: l?.p.photo,
  });
  if (!l) return <NotFound />;
  const { p, city, z } = l;
  const others = LANDING_PRODUCTS.filter((x) => x.key !== p.key);
  const cities = LANDING_CITIES.filter((c) => c !== city);

  return (
    <>
      <section className="k-section pt-12 grid gap-10 lg:grid-cols-12 items-start" data-testid="landing-page">
        <div className="lg:col-span-5">
          <p className="k-eyebrow">{z.eyebrow} · {z.name}</p>
          <h1 className="k-h1 mt-4" data-testid="landing-title">{title}</h1>
          <p className="k-lead mt-5">{p.intro}</p>
          <p className="text-sm text-[#57534E] mt-4 leading-relaxed">{z.lead}</p>
          <p className="text-sm mt-4">{p.from ? <><strong>{t("Dès")} {p.from} € HT</strong> {t("la pièce, broderie logo cœur comprise")} · </> : <><strong>{t("Prix sur devis")}</strong> · </>}{t("minimum 10 pièces")} · {t("devis gratuit sous 24 h")}</p>
          <GoogleBadge className="mt-3" />
          <div className="mt-6 aspect-[4/3] overflow-hidden bg-[#F4F0EA]"><img src={p.photo} alt={`${p.label} — ${z.name}`} className="w-full h-full object-cover object-[center_25%]" loading="lazy" /></div>
          <dl className="mt-8 grid gap-4" data-testid="landing-faq">
            {p.faq.map(([q, a]) => <div key={q} className="border-t border-[#E8E2D8] pt-3"><dt className="font-medium">{q}</dt><dd className="text-sm text-[#57534E] mt-1">{a}</dd></div>)}
          </dl>
        </div>
        <div className="lg:col-span-7 lg:sticky lg:top-24">
          <ExpressForm initialProduct={p.key} defaultOffer={p.key === "casquette" || p.key === "accessoires" ? "accessoires" : "vetements"} title={`${p.label} — ${t("mon devis")}`} />
        </div>
      </section>
      <section className="k-section pt-0" data-testid="landing-links">
        <p className="k-eyebrow">{t("Aussi à")} {z.name}</p>
        <div className="mt-3 flex flex-wrap gap-2">{others.map((o) => <Link key={o.key} to={`/personnalisation/${o.slug}-${city}`} className="px-3 py-1.5 text-sm border border-[#D6CEBF] hover:border-[#1C1917]">{o.label}</Link>)}</div>
        <p className="k-eyebrow mt-8">{p.label} {t("ailleurs sur la Côte d'Azur")}</p>
        <div className="mt-3 flex flex-wrap gap-2">{cities.map((c) => <Link key={c} to={`/personnalisation/${p.slug}-${c}`} className="px-3 py-1.5 text-sm border border-[#D6CEBF] hover:border-[#1C1917]">{ZONES[`zones/${c}`].name}</Link>)}</div>
        <p className="mt-6 text-sm"><Link to={`/zones/${city}`} className="k-link">{t("Tout ce que nous faisons à")} {z.name}</Link> · <Link to="/produits" className="k-link">{t("Voir le vestiaire")}</Link> <ArrowRight size={14} className="inline" /></p>
      </section>
      <Reviews />
    </>
  );
}
